import React from 'react';
import { Justify, SearchBox, TabPanel, Tabs, StatusTip } from 'tea-component';
import FilterCommon, { useFilterCommon } from '@opm/components/FilterCommon';
import MarketContentBody from '@opm/components/MarketContentBody';
import CardCommon from '@op/components/CardCommon';
import { cardIcon, tableIcon } from '@op/constants/chart';
import { ClassSpecial } from '@op/constants/common';
import SingleTranslateSelect from '@op/components/FilterBoxCommon/filters/languages/singleLauage';
import SortBySelect from '@op/components/FilterBoxCommon/filters/single-select-factory';
import { LanguageType } from '@op/types/common';
import { DBO_TM } from '@op/utils/format';
import { DBOM_POINT } from '@opm/operationLog/buriedPoint';
import { VideoPoint } from '@opm/operationLog/pages';
import useIntls from '@opm/hooks/useIntls';
import { useScrollBottomRegister } from '@opm/components/ScrollBottomList';
import { useBilibiliVideoData } from '../../hooks';
import { useVideoTranslate } from '../../context';
import BilibiliVideoCard from './BilibiliVideoCard';
import BilibiliVideoTable from './BilibiliVideoTable';
import { BILIBILI_SORT_OPTIONS } from './constants';
import './index.scss';

interface IBilibiliTabProps {
  backCondConfig: any;
  translateCategory: (value: string, isEn: boolean) => string;
}

/**
 * Videos · Bilibili Tab
 *
 * 结构（Figma 4222-67435 / 4222-68103）：
 *  1. FilterCommon: MultiDateRange + BilibiliCategory + BilibiliTag
 *  2. CardCommon: title + 操作区（Sort / Language / Search + card/table 切换 + Download）
 *  3. Tabs:
 *     - 'card': 4 列卡片网格 + 滚动到底加载更多
 *     - 'table': 7 列表格 + 翻页器
 */
export default function BilibiliTab({ backCondConfig }: IBilibiliTabProps) {
  // 页面进入埋点：tea Tabs destroyInactiveTabPanel=true，每次激活本 tab 都会重新触发
  DBOM_POINT.useInitPage({ buriedPoint: VideoPoint, pageExtInfo: 'bilibili' });

  const { intl } = useIntls();
  const { language, setLanguage } = useVideoTranslate();

  const { filterRef, filter, setFilter, loading, ...filterProps } =
    useFilterCommon({
      keys: ['MultiDateRange', 'BilibiliCategory', 'BilibiliTag'],
    });

  const {
    cardRecords,
    tableRecords,
    total,
    max,
    pageInfo,
    changePageInfo,
    nextPage,
    resetData,
    operation,
    setOperation,
    sortBy,
    setSortBy,
    setKeyword,
    download,
    loading: dataLoading,
  } = useBilibiliVideoData({
    filter: filterRef.current,
  });

  useScrollBottomRegister(nextPage);

  const handleConfirm = (value: any) => {
    setFilter(value);
    resetData();
  };

  return (
    <MarketContentBody
      loading={loading}
      filterSlot={
        <FilterCommon
          {...filterProps}
          userDefaultValue={{
            ...DBO_TM.filterRangeDay(6, 0),
            date_type: 'daily',
          }}
          realTimeMode
          confirm={handleConfirm}
          backCondConfig={backCondConfig}
        />
      }
    >
      <CardCommon
        className="dbom-bili-tab-card"
        alwaysShowOperations
        title={intl('Trending Videos')}
        defaultOperation={operation}
        operations={[cardIcon, tableIcon]}
        hasDownload
        handleOperationsOnClick={(item: any) => {
          setOperation(item.symbol);
        }}
        handleDownloadOnClick={download}
      >
        <Justify
          left={
            <div className="dbom-bili-tab-filter">
              <SingleTranslateSelect
                defaultValue={language}
                onChange={(_key: string, value: LanguageType) =>
                  setLanguage(value)
                }
              />
              <SortBySelect
                key="sortBy"
                defaultValue={sortBy?.by}
                onChange={(_key: any, value: any) => {
                  setSortBy({ order: 'desc', by: value });
                }}
                defaultParams={{
                  btnTextPrefix: intl({ id: 'Sort By' }),
                  options: BILIBILI_SORT_OPTIONS
                }}
              />
            </div>
          }
          right={
            <SearchBox
              onSearch={(value = '') => setKeyword(value)}
              onClear={() => setKeyword('')}
              placeholder={intl({ id: 'Search video' })}
            />
          }
        />

        <Tabs activeId={operation} className={ClassSpecial.TAB_NONE}>
          <TabPanel id="card">
            <BilibiliVideoCardList
              language={language}
              cardRecords={cardRecords}
              loading={dataLoading}
            />
          </TabPanel>
          <TabPanel id="table">
            <BilibiliVideoTable
              sortBy={sortBy}
              setSortBy={setSortBy}
              language={language}
              tableRecords={tableRecords}
              loading={dataLoading}
              total={total}
              pageInfo={pageInfo}
              changePageInfo={changePageInfo}
              max={max}
            />
          </TabPanel>
        </Tabs>
      </CardCommon>
    </MarketContentBody>
  );
}

interface IBilibiliVideoCardListProps {
  language: LanguageType;
  cardRecords: any[];
  loading?: boolean;
}

/**
 * 卡片视图容器 — 4 列网格 + 加载占位
 */
function BilibiliVideoCardList({
  language,
  cardRecords,
  loading,
}: IBilibiliVideoCardListProps) {
  const { intl } = useIntls();

  return (
    <CardCommon
      contentCard
      loading={loading && cardRecords.length === 0}
      loadingHeight={350}
      empty={cardRecords.length === 0}
      emptyHeight={350}
      className='dbom-bili-video-card-list'
    >
      <>
        <div className="dbom-bili-video-card-list-content">
          {cardRecords.map((item: any, index: number) => (
            <BilibiliVideoCard
              key={item?.video_id || index}
              item={item}
              language={language}
            />
          ))}
        </div>
        {loading && cardRecords.length !== 0 && (
          <div className="loading-common dbom-loading-common">
            <StatusTip status="loading" loadingText={intl('Loading...')} />
          </div>
        )}
      </>
    </CardCommon>
  );
}
